import React, { useCallback, useMemo, useState } from 'react';
import { ClipboardList, ListTodo } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { haptic } from '../utils/vibration';
import DeferredOrdersQueue from './DeferredOrdersQueue';
import DeferredTasksQueue from './DeferredTasksQueue';

const CountBadge = ({ value }) => (
  <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
    {value === null ? '…' : value}
  </span>
);

export default function DeferredWorkQueue({ canAccessTasks = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ orders: null, tasks: null });
  const requestedTab = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return params.get('deferred_tab') === 'tasks' ? 'tasks' : 'orders';
  }, [location.search]);
  const activeTab = requestedTab === 'tasks' && canAccessTasks ? 'tasks' : 'orders';
  const handleOrdersCountChange = useCallback((value) => {
    setCounts((prev) => (prev.orders === value ? prev : { ...prev, orders: value }));
  }, []);
  const handleTasksCountChange = useCallback((value) => {
    setCounts((prev) => (prev.tasks === value ? prev : { ...prev, tasks: value }));
  }, []);

  const selectTab = (tab) => {
    haptic('selection');
    const params = new URLSearchParams(location.search || '');
    params.set('tab', 'my');
    params.set('stage', 'deferred');
    params.set('deferred_tab', tab);
    navigate(`/my-orders?${params.toString()}`, { replace: true });
  };

  const tabs = [
    { id: 'orders', label: 'Заказы', icon: ClipboardList, count: counts.orders },
    ...(canAccessTasks ? [{ id: 'tasks', label: 'Поручения', icon: ListTodo, count: counts.tasks }] : []),
  ];

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-xl bg-white shadow-sm dark:bg-gray-900">
        <div
          className="grid border-b border-gray-200 dark:border-gray-700"
          style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => selectTab(tab.id)}
                className={`flex items-center justify-center gap-2 border-b-2 px-2 py-3 text-sm font-medium transition-colors ${activeTab === tab.id
                  ? 'border-amber-500 text-amber-700 dark:text-amber-300'
                  : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'}`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
                <CountBadge value={tab.count} />
              </button>
            );
          })}
        </div>
      </div>

      <div className={activeTab === 'orders' ? '' : 'hidden'} aria-hidden={activeTab !== 'orders'}>
        <DeferredOrdersQueue onCountChange={handleOrdersCountChange} />
      </div>
      {canAccessTasks ? (
        <div className={activeTab === 'tasks' ? '' : 'hidden'} aria-hidden={activeTab !== 'tasks'}>
          <DeferredTasksQueue onCountChange={handleTasksCountChange} />
        </div>
      ) : null}
    </div>
  );
}
